/*
================================================================================
FILE HEADER — apps/studio-web/src/components/BrowserMirror.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                 | What Changed          |
|----|------------|-------------------|---------------------|-----------------------|
| 1  | 2026-06-23 | Claude Sonnet 4.6 | Phase 0             | Initial canvas mirror |
| 2  | 2026-06-23 | Claude Sonnet 4.6 | Simplify + fix      | Refs for guards       |
| 3  | 2026-06-25 | Claude Sonnet 4.6 | Option B            | staticImageUrl overlay|
| 4  | 2026-06-25 | Claude Sonnet 4.6 | Always-on           | FORWARD_CLICK/SCROLL  |
| 5  | 2026-06-25 | Claude Sonnet 4.6 | Scroll rewrite      | ALL pointer + wheel   |
|    |            |                   |                     | handling via native   |
|    |            |                   |                     | addEventListener.     |
|    |            |                   |                     | React onPointerUp     |
|    |            |                   |                     | broke with            |
|    |            |                   |                     | setPointerCapture.   |
| 6  | 2026-07-03 | Claude Sonnet 5   | AI Assist "Element  | Added                 |
|    |            |                   | Captured" pulse     | elementCapturedBounds |
|    |            |                   | (feature request)   | prop — percentage-    |
|    |            |                   |                     | positioned pulsating  |
|    |            |                   |                     | highlight box, no new |
|    |            |                   |                     | bridge round-trip     |
|    |            |                   |                     | needed since node.    |
|    |            |                   |                     | bounds already arrives|
|    |            |                   |                     | with ELEMENT_CAPTURED |

IMPORTANT POINTS
----------------
1. EVERY canvas interaction (click, drag-scroll, wheel-scroll, keyboard) is handled
   via native addEventListener with { passive: false }, NOT React synthetic events.
   Reason: React's event delegation and setPointerCapture conflict in React 17+,
   causing onPointerUp to never fire after setPointerCapture is called.

2. Tap-vs-drag detection (Mimiq pattern):
   pointerdown → save start. pointerup → if dist < 15px = tap (click), else = scroll.
   setPointerCapture ensures pointerup fires even if mouse leaves the canvas.

3. Scroll direction:
   Drag DOWN (dPageY > 0) → FORWARD_SCROLL with positive deltaY → window.scrollBy(0, +dy) → scroll DOWN ✓
   Wheel DOWN (e.deltaY > 0) → FORWARD_SCROLL with positive deltaY → same ✓

4. captureMode (Command Builder) and Inspect mode still send CAPTURE_ELEMENT_AT,
   which goes through the pointer listener, no click forwarded.
================================================================================
*/

import { useEffect, useRef, useCallback, useState } from 'react';
import type { ExtensionBridge } from '../extension/bridge.js';

// ── Ripple type ───────────────────────────────────────────────────────────────

interface Ripple { id: number; cx: number; cy: number; }
let _rippleCounter = 0;
const nextRippleId = () => ++_rippleCounter;

const SWIPE_THRESHOLD = 15; // CSS pixels — below = tap, at/above = scroll drag

// ── Props ─────────────────────────────────────────────────────────────────────

type Props = {
  bridge:             ExtensionBridge;
  isRecording:        boolean;
  isMirroring:        boolean;
  isAttaching:        boolean;
  attachingTitle:     string;
  captureWidth:       number;
  captureHeight:      number;
  staticImageUrl?:    string | null;
  staticImageLabel?:  string;
  captureMode?:       boolean;
  mode?:              'drive' | 'inspect';
  highlighterEnabled?: boolean;
  highlighterColor?:   string;
  /** AI Assist "🎯 Pick element" feedback — pass a NEW object each time
   *  (even for identical bounds) to re-trigger the pulse animation. */
  elementCapturedBounds?: { x: number; y: number; width: number; height: number } | null;
};

// ── Component ─────────────────────────────────────────────────────────────────

export function BrowserMirror({
  bridge, isRecording, isMirroring, isAttaching, attachingTitle,
  staticImageUrl, staticImageLabel, captureMode = false,
  mode = 'drive',
  highlighterEnabled = true, highlighterColor = '#7c3aed',
  elementCapturedBounds = null,
}: Props) {
  const canvasRef      = useRef<HTMLCanvasElement>(null);
  const isRecordingRef = useRef(isRecording);
  const isMirroringRef = useRef(isMirroring);
  const isAttachingRef = useRef(isAttaching);
  const captureModeRef = useRef(captureMode);
  const modeRef        = useRef(mode);
  const highlighterRef = useRef({ enabled: highlighterEnabled, color: highlighterColor });
  const cssVpRef       = useRef({ w: 1366, h: 768 });

  const [ripples,     setRipples]    = useState<Ripple[]>([]);
  const [scrollArrow, setScrollArrow] = useState<{ dx: number; dy: number } | null>(null);
  const scrollArrowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [captureFlash, setCaptureFlash] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const captureFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // "Element Captured" pulse — re-triggers whenever the parent passes a new
  // elementCapturedBounds object (App.tsx constructs a fresh one per capture).
  useEffect(() => {
    if (!elementCapturedBounds) return;
    setCaptureFlash(elementCapturedBounds);
    if (captureFlashTimer.current) clearTimeout(captureFlashTimer.current);
    captureFlashTimer.current = setTimeout(() => setCaptureFlash(null), 1400);
    return () => { if (captureFlashTimer.current) clearTimeout(captureFlashTimer.current); };
  }, [elementCapturedBounds]);

  // Keep refs in sync with props
  useEffect(() => { isRecordingRef.current  = isRecording;  }, [isRecording]);
  useEffect(() => { isMirroringRef.current  = isMirroring;  }, [isMirroring]);
  useEffect(() => { isAttachingRef.current  = isAttaching;  }, [isAttaching]);
  useEffect(() => { captureModeRef.current  = captureMode;  }, [captureMode]);
  useEffect(() => { modeRef.current         = mode;         }, [mode]);
  useEffect(() => { highlighterRef.current  = { enabled: highlighterEnabled, color: highlighterColor }; },
    [highlighterEnabled, highlighterColor]);

  // Clear canvas when mirroring stops
  useEffect(() => {
    if (!isMirroring || isAttaching) {
      const canvas = canvasRef.current;
      if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [isMirroring, isAttaching]);

  // Paint MIRROR_FRAME events to canvas
  useEffect(() => {
    const img = new Image();
    const unsub = bridge.on((msg) => {
      if (msg.type !== 'MIRROR_FRAME') return;
      if (!isMirroringRef.current || isAttachingRef.current) return;
      cssVpRef.current = { w: msg.vw, h: msg.vh };
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      img.onload = () => {
        if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
          canvas.width  = img.naturalWidth;
          canvas.height = img.naturalHeight;
        }
        ctx.drawImage(img, 0, 0);
      };
      img.src = msg.dataUrl;
    });
    return unsub;
  }, [bridge]);

  // Helper: add ripple at canvas-display position
  const addRipple = useCallback((cx: number, cy: number) => {
    if (!highlighterRef.current.enabled) return;
    const id = nextRippleId();
    setRipples(prev => [...prev, { id, cx, cy }]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 700);
  }, []);

  // Show scroll arrow indicator
  const showScrollArrow = useCallback((dx: number, dy: number) => {
    if (!highlighterRef.current.enabled) return;
    setScrollArrow({ dx, dy });
    if (scrollArrowTimer.current) clearTimeout(scrollArrowTimer.current);
    scrollArrowTimer.current = setTimeout(() => setScrollArrow(null), 900);
  }, []);

  // ── ALL canvas interactions via native event listeners ─────────────────────
  // This avoids React synthetic event issues with setPointerCapture.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Helper: convert canvas-display coords → CDP CSS pixel coords
    const toPage = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      const { w: vw, h: vh } = cssVpRef.current;
      return {
        pageX: Math.round((clientX - rect.left) / rect.width  * vw),
        pageY: Math.round((clientY - rect.top)  / rect.height * vh),
        cssX:  clientX - rect.left,
        cssY:  clientY - rect.top,
      };
    };

    // Track gesture start for tap-vs-drag detection
    let gesture: { pageX: number; pageY: number; cssX: number; cssY: number; t: number } | null = null;

    // ── POINTER DOWN ─────────────────────────────────────────────────────────
    // Always save gesture start — we decide tap vs drag at pointerUp.
    // Do NOT fire any action here; capture/inspect/drive all handled on pointerUp
    // so that a drag (scroll) in inspect/capture mode doesn't accidentally fire
    // CAPTURE_ELEMENT_AT.
    const onPointerDown = (e: PointerEvent) => {
      if (isAttachingRef.current) return;
      e.preventDefault();
      const pos = toPage(e.clientX, e.clientY);
      canvas.focus({ preventScroll: true });
      canvas.setPointerCapture(e.pointerId); // keeps pointerup even if mouse exits canvas
      gesture = { pageX: pos.pageX, pageY: pos.pageY, cssX: pos.cssX, cssY: pos.cssY, t: Date.now() };
    };

    // ── POINTER UP ───────────────────────────────────────────────────────────
    const onPointerUp = (e: PointerEvent) => {
      const gs = gesture;
      gesture = null;
      if (!gs || isAttachingRef.current) return;

      const pos = toPage(e.clientX, e.clientY);
      const dCssX = pos.cssX - gs.cssX;
      const dCssY = pos.cssY - gs.cssY;
      const dist  = Math.sqrt(dCssX * dCssX + dCssY * dCssY);

      if (dist >= SWIPE_THRESHOLD) {
        // ── DRAG → SCROLL (all modes) ───────────────────────────────────────
        // dPageY is viewport-pixel delta already scaled by toPage().
        // background.ts uses window.scrollBy(dx, dy) directly — NO extra factor.
        const dPageX = pos.pageX - gs.pageX;
        const dPageY = pos.pageY - gs.pageY;
        // Grab-and-pan: drag DOWN = pull content down = page scrolls UP = see content above.
        // Negate delta so drag direction matches physical content movement.
        // Arrow also negated so it shows SCROLL direction (where content is going), not drag direction.
        bridge.send({ type: 'FORWARD_SCROLL', x: gs.pageX, y: gs.pageY, deltaX: -dPageX, deltaY: -dPageY, isDrag: true });
        showScrollArrow(-dCssX, -dCssY);
      } else if (captureModeRef.current || modeRef.current === 'inspect') {
        // ── TAP in capture/inspect → identify element ──────────────────────
        bridge.send({ type: 'CAPTURE_ELEMENT_AT', x: gs.pageX, y: gs.pageY });
        addRipple(gs.cssX, gs.cssY);
      } else if (isRecordingRef.current) {
        // ── TAP in drive+recording → record step ───────────────────────────
        bridge.send({ type: 'RECORD_AND_CLICK', x: gs.pageX, y: gs.pageY });
        addRipple(gs.cssX, gs.cssY);
      } else {
        // ── TAP in drive (not recording) → forward click ───────────────────
        bridge.send({ type: 'FORWARD_CLICK', x: gs.pageX, y: gs.pageY });
        addRipple(gs.cssX, gs.cssY);
      }
    };

    const onPointerCancel = () => { gesture = null; };

    // ── WHEEL SCROLL ─────────────────────────────────────────────────────────
    // Chrome generates 10–30 individual WheelEvents per physical scroll notch
    // (smooth scroll mode). Sending one CDP command per event floods the extension
    // and causes the page to scroll violently as the queue drains.
    // Fix: accumulate all wheel deltas for 120ms then send ONE combined command.
    let wheelAccX = 0, wheelAccY = 0;
    let wheelPos = { x: 0, y: 0 };
    let wheelTimer: ReturnType<typeof setTimeout> | null = null;

    const flushWheel = () => {
      if (wheelAccX !== 0 || wheelAccY !== 0) {
        bridge.send({ type: 'FORWARD_SCROLL', x: wheelPos.x, y: wheelPos.y, deltaX: wheelAccX, deltaY: wheelAccY });
        showScrollArrow(wheelAccX, wheelAccY);
        wheelAccX = 0;
        wheelAccY = 0;
      }
      wheelTimer = null;
    };

    const onWheel = (e: WheelEvent) => {
      if (!isMirroringRef.current || isAttachingRef.current) return;
      e.preventDefault();
      e.stopPropagation();

      wheelAccX += e.deltaX;
      wheelAccY += e.deltaY;
      const pos = toPage(e.clientX, e.clientY);
      wheelPos = { x: pos.pageX, y: pos.pageY };

      if (!wheelTimer) {
        wheelTimer = setTimeout(flushWheel, 120);
      }
    };

    // ── KEYBOARD ─────────────────────────────────────────────────────────────
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isRecordingRef.current || isAttachingRef.current) return;
      if (e.ctrlKey && ['r','l','t','w','n'].includes(e.key.toLowerCase())) return;
      if (e.key === 'F5' || e.key === 'F12') return;
      e.preventDefault();
      const modifiers =
        (e.shiftKey ? 8 : 0) | (e.ctrlKey ? 2 : 0) |
        (e.altKey   ? 1 : 0) | (e.metaKey  ? 4 : 0);
      bridge.send({ type: 'FORWARD_KEY', key: e.key, code: e.code, modifiers });
    };

    canvas.addEventListener('pointerdown',   onPointerDown);
    canvas.addEventListener('pointerup',     onPointerUp);
    canvas.addEventListener('pointercancel', onPointerCancel);
    canvas.addEventListener('wheel',         onWheel,   { passive: false });
    canvas.addEventListener('keydown',       onKeyDown);

    return () => {
      canvas.removeEventListener('pointerdown',   onPointerDown);
      canvas.removeEventListener('pointerup',     onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerCancel);
      canvas.removeEventListener('wheel',         onWheel);
      canvas.removeEventListener('keydown',       onKeyDown);
      if (wheelTimer) clearTimeout(wheelTimer);
    };
  }, [bridge, addRipple, showScrollArrow]); // stable callbacks

  // ── Cursor style ──────────────────────────────────────────────────────────
  const cursor = captureMode ? 'crosshair' : isMirroring && !isAttaching ? 'pointer' : 'default';
  const showAttaching = isAttaching;
  const showIdle      = !isMirroring && !isAttaching && !staticImageUrl;

  const scrollGlyph = scrollArrow
    ? (Math.abs(scrollArrow.dy) >= Math.abs(scrollArrow.dx)
        ? (scrollArrow.dy > 0 ? '↓' : '↑')
        : (scrollArrow.dx > 0 ? '→' : '←'))
    : null;

  return (
    <div style={{
      width: '100%', background: '#111113', borderRadius: 8,
      overflow: 'hidden', position: 'relative',
      boxShadow: (isMirroring || staticImageUrl) ? '0 0 0 2px #7c3aed' : '0 0 0 1px #27272a',
    }}>
      {/* Live canvas — all events handled via native listeners above */}
      <canvas
        ref={canvasRef}
        width={800} height={600}
        tabIndex={0}
        style={{
          width: '100%', height: 'auto', display: 'block',
          background: '#0f0f11', cursor,
          outline: 'none',
          visibility: staticImageUrl ? 'hidden' : 'visible',
        }}
      />

      {/* Static screenshot overlay (step snapshots / relay) */}
      {staticImageUrl && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
          <img src={staticImageUrl} alt="Step screenshot"
            style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain', background: '#0f0f11' }} />
          {staticImageLabel && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'rgba(0,0,0,0.72)', padding: '5px 10px',
              fontSize: 11, color: '#a1a1aa', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#7c3aed', flexShrink: 0 }} />
              {staticImageLabel}
            </div>
          )}
        </div>
      )}

      {/* Capture mode overlay */}
      {captureMode && isMirroring && !isAttaching && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          background: 'rgba(34,197,94,0.12)', borderBottom: '2px solid #22c55e',
          padding: '6px 12px', fontSize: 11, color: '#22c55e', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none',
        }}>
          <span style={{ fontSize: 14 }}>🎯</span>
          Click any element on the mirror to capture it for your command
        </div>
      )}

      {/* AI Assist "Element Captured" pulse — percentage-positioned against the
          same viewport coordinate space toPage() uses, so it tracks the mirror's
          on-screen size without a getBoundingClientRect() re-measure. */}
      {captureFlash && (() => {
        const { w: vw, h: vh } = cssVpRef.current;
        if (vw <= 0 || vh <= 0) return null;
        const leftPct   = (captureFlash.x / vw) * 100;
        const topPct    = (captureFlash.y / vh) * 100;
        const widthPct  = (captureFlash.width  / vw) * 100;
        const heightPct = (captureFlash.height / vh) * 100;
        return (
          <div style={{
            position: 'absolute',
            left: `${leftPct}%`, top: `${topPct}%`,
            width: `${widthPct}%`, height: `${heightPct}%`,
            border: '3px solid #22c55e', borderRadius: 4,
            pointerEvents: 'none',
            animation: 'plexElementCapturedPulse 1.4s ease-out forwards',
          }}>
            <div style={{
              position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
              background: '#22c55e', color: '#052e16', fontSize: 10, fontWeight: 700,
              padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
            }}>
              ✓ Element Captured
            </div>
          </div>
        );
      })()}

      {/* Click ripples */}
      {ripples.map(r => (
        <div key={r.id} style={{ position: 'absolute', left: r.cx, top: r.cy, width: 0, height: 0, pointerEvents: 'none' }}>
          <div style={{
            position: 'absolute', transform: 'translate(-50%, -50%)',
            width: 40, height: 40, borderRadius: '50%',
            border: `3px solid ${highlighterColor}`,
            animation: 'plexRipple 0.65s ease-out forwards',
          }} />
          <div style={{ position: 'absolute', transform: 'translate(-50%, -50%)', width: 8, height: 8, borderRadius: '50%', background: highlighterColor, opacity: 0.9 }} />
        </div>
      ))}

      {/* Scroll direction arrow */}
      {scrollGlyph && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: 48, color: highlighterColor, fontWeight: 900,
          textShadow: `0 0 20px ${highlighterColor}99`,
          pointerEvents: 'none', animation: 'plexScrollArrow 0.9s ease-out forwards',
        }}>
          {scrollGlyph}
        </div>
      )}

      {/* Attaching overlay */}
      {showAttaching && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 10, background: '#0f0f11',
        }}>
          <div style={{ width: 28, height: 28, border: '3px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          <div style={{ color: '#a1a1aa', fontSize: 13 }}>
            Connecting to <strong style={{ color: '#e4e4e7' }}>{attachingTitle}</strong>…
          </div>
        </div>
      )}

      {/* Idle overlay */}
      {showIdle && (
        <div style={{
          position: 'absolute', inset: 0, minHeight: 200,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 8,
          color: '#3f3f46', pointerEvents: 'none',
        }}>
          <div style={{ fontSize: 32 }}>⏺</div>
          <div style={{ fontSize: 13 }}>Click <strong>Start Mirror</strong> to connect a browser tab</div>
        </div>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes plexRipple { 0% { width:0; height:0; opacity:1; } 100% { width:80px; height:80px; opacity:0; border-width:1px; } }
        @keyframes plexScrollArrow { 0% { opacity:1; transform:translate(-50%,-50%) scale(1); } 70% { opacity:0.7; transform:translate(-50%,-50%) scale(1.2); } 100% { opacity:0; transform:translate(-50%,-50%) scale(0.8); } }
        @keyframes plexElementCapturedPulse {
          0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.6); }
          30%  { box-shadow: 0 0 0 8px rgba(34,197,94,0.15); }
          60%  { box-shadow: 0 0 0 4px rgba(34,197,94,0.35); }
          100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
